import Modal from "@via/components/modal.tsx";
import {
  useDeleteProject,
  useProjectRunList,
} from "@via/pages/projects/projects.api.tsx";
import { ButtonColor } from "@via/utils/constants.tsx";
import { type ListProjectsResponseBodyItem } from "common";
import { type FC } from "react";

interface DeleteProjectModalProps {
  project: ListProjectsResponseBodyItem;
  setProject: (project: ListProjectsResponseBodyItem | null) => void;
}

const DeleteProjectModal: FC<DeleteProjectModalProps> = ({
  project,
  setProject,
}) => {
  const { mutateAsync: deleteProject, isPending: isDeleteLoading } =
    useDeleteProject();
  const { isLoading: isRunLoading, data: runData } = useProjectRunList(
    project.id.toString()
  );

  const handleDeleteProject = async (id: string): Promise<void | boolean> => {
    // Returning false keeps the dialog open. Resolving closes it through Modal's
    // setOpen, which is what clears the selected project, so there is one close
    // path rather than a success effect racing the dialog.
    if (!id) return false;
    await deleteProject(id);
  };

  return (
    <Modal
      open={!!project}
      setOpen={() => {
        setProject(null);
      }}
      title="Delete Project"
      confirmText="Delete"
      confirmButtonColor={ButtonColor.Red}
      isConfirmLoading={isDeleteLoading || isRunLoading}
      onConfirm={() => handleDeleteProject(project.id.toString())}>
      <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3 lg:py-4">
        <p>
          Are you sure you want to delete project{" "}
          <span className="font-semibold">{project?.name}</span>?
        </p>
        {!!runData?.data?.length && (
          <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3 lg:py-4">
            <p>Following runs will be deleted as well:</p>
            <ul>
              {runData.data.map(({ id, name }) => (
                <li key={id}>- {name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DeleteProjectModal;
